/**
 * `<CycleSurfaceModal/>` — the shared shell of every roster `?surface=`
 * (the ladder, Coverage, Fairness, Hours, Demand, Swaps, Submissions, the
 * meeting finder, Schedule setup, Relaxation rules …).
 *
 * **A side panel, not a modal (#1922).** The founder (2 Oct 2026): these
 * pop-outs blacked out the page behind them, and the page behind is exactly
 * what the lead decides against — which day is short, who else is free,
 * what the column header says. So on a desktop the default presentation is
 * the non-modal right-edge panel (`ui/SideDrawer` via `ResponsiveModal
 * presentation="panel"`): no scrim, no focus trap, the grid behind stays
 * readable, scrollable and clickable, and the panel stays open across a
 * scroll until its ✕, `Esc` or the chip that opened it closes it. Opening
 * one closes the lead's next-steps drawer and any other open panel — one
 * drawer slot (`lib/drawerSlot`). Widths: `lg` 480 px for a decision
 * against one place on the page (the ladder, Schedule setup), `xl` 720 px
 * for the wide reads (Coverage, Fairness, Hours, Demand, Swaps …).
 *
 * `presentation="modal"` keeps the centred dialog with its (light) scrim —
 * for a confirm whose whole content is in the box (Make this the team
 * default). On a phone every surface is the `BottomSheet`; `snap="half"`
 * opens it at half height with the page above usable (the ladder).
 *
 * The shell is controlled: the parent (`CycleWorkspaceShell`, the roster
 * grid for the ladder) owns which surface is open and the `?surface=X`
 * deep-link wiring, which is unchanged.
 *
 * Brand-pillar discipline: titles + descriptions must stay state-
 * mechanic. No compounded-banlist words. The caller picks the title
 * copy; this shell just renders it.
 */

import { type ReactNode, type RefObject, createContext, useContext } from "react";

import { cn } from "../../lib/cn";
import type { BottomSheetSnap } from "../ui/BottomSheet";
import { ResponsiveModal, type ResponsiveModalPresentation } from "../ui/ResponsiveModal";
import type { SideDrawerWidth } from "../ui/SideDrawer";

/**
 * The element that opened the current surface, for the panel to hand focus
 * back to on close. `CycleWorkspaceShell` provides it around its toolbar
 * surfaces: a "More" menu item unmounts as the panel opens, so the element
 * that held focus at open is gone by close — the menu's trigger is the
 * opener the user knows. An explicit `returnFocusRef` prop wins.
 *
 * #1934 — the ref may be a getter: the shell resolves it at close time to
 * the opener if it is still on the page, else the control registered for
 * the closing surface (a deep-link open has no opener).
 */
export const SurfaceReturnFocusContext = createContext<RefObject<HTMLElement | null> | null>(null);

export interface CycleSurfaceModalProps {
  /** Controls open/close state. The shell is a controlled component so
   *  the parent (CycleWorkspaceShell) owns the surface registry + the
   *  ?surface=X deep-link wiring. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title shown at the top of the surface. Keep state-mechanic; this is
   *  user-visible copy. */
  title: string;
  /** Optional one-line subtitle below the title. */
  description?: string | undefined;
  /** Surface body. */
  children: ReactNode;
  /** testid for the surface container (parent passes a per-surface id so
   *  tests can latch on by surface); the body is `${testid}-body`. */
  testid: string;
  /** `presentation="modal"` only: a className override for the centred
   *  DialogContent (its max-width). The panel takes `panelWidth`. */
  contentClassName?: string | undefined;
  /** Desktop: `"panel"` (default, #1922) or `"modal"` for a confirm. */
  presentation?: ResponsiveModalPresentation | undefined;
  /** Desktop panel width: `lg` 480 px (default) · `xl` 720 px · `md` 360. */
  panelWidth?: SideDrawerWidth | undefined;
  /** Phone sheet: `"half"` opens at half height with no scrim. */
  snap?: BottomSheetSnap | undefined;
  /** Desktop panel: where focus returns on close, when the opener is not
   *  the element that held focus at open (a menu item that unmounts). */
  returnFocusRef?: RefObject<HTMLElement | null> | undefined;
  /** Desktop panel: where focus goes on close when the opener is gone (a
   *  deep-link open has none) — the control that opens this surface, e.g.
   *  the ladder's day chip (#1934). */
  fallbackFocusRef?: RefObject<HTMLElement | null> | undefined;
}

export function CycleSurfaceModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  testid,
  contentClassName,
  presentation = "panel",
  panelWidth = "lg",
  snap,
  returnFocusRef,
  fallbackFocusRef,
}: CycleSurfaceModalProps) {
  const contextReturnFocusRef = useContext(SurfaceReturnFocusContext);
  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      testid={testid}
      presentation={presentation}
      panelWidth={panelWidth}
      snap={snap}
      returnFocusRef={returnFocusRef ?? contextReturnFocusRef ?? undefined}
      fallbackFocusRef={fallbackFocusRef}
      // The centred dialog is wider than the default `max-w-lg` — these
      // surfaces carry tables. Panel and phone sheet ignore it.
      desktopContentClassName={cn("max-w-5xl", contentClassName)}
    >
      {children}
    </ResponsiveModal>
  );
}

export default CycleSurfaceModal;
